import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Home, Search, Ticket, Package, User, Sun, Moon } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { t } from '@/i18n/translations'

const UltraResponsiveNav: React.FC = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { isDark, toggleTheme } = useTheme()
  const { lang } = useLanguage()

  const NAV_ITEMS = [
    { id: 'home',     Icon: Home,    route: '/home',        label: t(lang, 'nav_home') },
    { id: 'search',   Icon: Search,  route: '/reservation', label: t(lang, 'nav_search') },
    { id: 'tickets',  Icon: Ticket,  route: '/tickets',     label: t(lang, 'nav_tickets') },
    { id: 'packages', Icon: Package, route: '/packages',    label: t(lang, 'nav_packages') },
    { id: 'profile',  Icon: User,    route: '/profile',     label: t(lang, 'nav_profile') },
  ]

  const isActive = (route: string) => pathname === route

  return (
    <>
      {/* ── Mobile nav ─────────────────────────────────── */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 340, damping: 28, delay: 0.15 }}
        className="fixed bottom-4 left-4 right-4 z-50 lg:hidden"
      >
        <div style={{ position: 'relative' }}>
          {/* Ambient glow */}
          <div
            style={{
              position: 'absolute', inset: 0, borderRadius: 28,
              background: `var(--sl-nav-glow)`,
              filter: 'blur(20px)',
              pointerEvents: 'none',
            }}
          />

          {/* Nav pill */}
          <div
            style={{
              position: 'relative',
              background: 'var(--sl-nav-bg)',
              border: '1px solid var(--sl-nav-border)',
              borderRadius: 28,
              backdropFilter: 'blur(32px)',
              WebkitBackdropFilter: 'blur(32px)',
              padding: '10px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {/* Accent top line */}
            <div
              style={{
                position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                width: 48, height: 2, borderRadius: 2,
                background: 'linear-gradient(90deg, var(--sl-accent), var(--sl-accent-bright))',
              }}
            />

            {/* Nav items */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 2 }}>
              {NAV_ITEMS.map(({ id, Icon, route, label }, index) => {
                const active = isActive(route)
                return (
                  <motion.button
                    key={id}
                    layout
                    onClick={() => navigate(route)}
                    aria-label={label}
                    whileTap={{ scale: 0.90 }}
                    initial={{ opacity: 0, y: 14, scale: 0.85 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 420, damping: 26, delay: index * 0.06 }}
                    style={{
                      position: 'relative',
                      height: 46,
                      borderRadius: 16,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: active ? 7 : 0,
                      paddingLeft: active ? 16 : 0,
                      paddingRight: active ? 16 : 0,
                      minWidth: active ? undefined : 46,
                      width: active ? undefined : 46,
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      flexShrink: active ? 1 : 0,
                      overflow: 'hidden',
                    }}
                  >
                    {active && (
                      <motion.div
                        layoutId="activeTabMobile"
                        transition={{ type: 'spring', stiffness: 500, damping: 32 }}
                        style={{
                          position: 'absolute', inset: 0, borderRadius: 16,
                          background: 'linear-gradient(135deg, var(--sl-accent) 0%, var(--sl-accent-bright) 100%)',
                          boxShadow: '0 4px 14px var(--sl-accent-muted)',
                        }}
                      />
                    )}
                    <div style={{ position: 'relative', zIndex: 1, flexShrink: 0 }}>
                      <Icon
                        style={{
                          width: 21, height: 21,
                          color: active ? '#fff' : 'var(--sl-nav-icon)',
                          transition: 'color 0.2s ease',
                        }}
                        strokeWidth={active ? 2.5 : 2}
                      />
                    </div>
                    <AnimatePresence>
                      {active && (
                        <motion.span
                          key="label"
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: 'auto' }}
                          exit={{ opacity: 0, width: 0 }}
                          transition={{ type: 'spring', stiffness: 380, damping: 28 }}
                          style={{
                            position: 'relative', zIndex: 1,
                            fontSize: 12, fontWeight: 700,
                            color: '#fff',
                            fontFamily: "'Jost', sans-serif",
                            letterSpacing: '0.01em',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                          }}
                        >
                          {label}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.button>
                )
              })}
            </div>

            {/* Theme toggle */}
            <div
              style={{
                width: 1, alignSelf: 'stretch',
                background: 'var(--sl-surface-border)',
                marginLeft: 4, marginRight: 4, flexShrink: 0,
              }}
            />
            <motion.button
              whileTap={{ scale: 0.85, rotate: isDark ? 30 : -30 }}
              onClick={toggleTheme}
              aria-label={isDark ? t(lang, 'nav_light') : t(lang, 'nav_dark')}
              style={{
                width: 44, height: 44, borderRadius: 14, flexShrink: 0,
                background: 'var(--sl-accent-muted)',
                border: '1px solid var(--sl-accent-border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              {isDark
                ? <Sun style={{ width: 18, height: 18, color: 'var(--sl-accent)' }} />
                : <Moon style={{ width: 18, height: 18, color: 'var(--sl-accent)' }} />
              }
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* ── Desktop nav ────────────────────────────────── */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 340, damping: 28, delay: 0.15 }}
        className="hidden lg:flex fixed bottom-8 inset-x-0 z-50 justify-center items-center pointer-events-none"
      >
        <div style={{ pointerEvents: 'auto', position: 'relative' }}>
          <div
            style={{
              position: 'absolute', inset: 0, borderRadius: 28,
              background: 'var(--sl-nav-glow)',
              filter: 'blur(24px)', pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'relative',
              background: 'var(--sl-nav-bg)',
              border: '1px solid var(--sl-nav-border)',
              borderRadius: 28,
              backdropFilter: 'blur(32px)',
              WebkitBackdropFilter: 'blur(32px)',
              padding: '14px 20px',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <div
              style={{
                position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                width: 60, height: 2, borderRadius: 2,
                background: 'linear-gradient(90deg, var(--sl-accent), var(--sl-accent-bright))',
              }}
            />

            {NAV_ITEMS.map(({ id, Icon, route, label }, index) => {
              const active = isActive(route)
              return (
                <motion.button
                  key={id}
                  onClick={() => navigate(route)}
                  aria-label={label}
                  whileTap={{ scale: 0.93 }}
                  initial={{ opacity: 0, y: 14, scale: 0.85 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 26, delay: index * 0.06 }}
                  style={{
                    position: 'relative', minWidth: 80, borderRadius: 18,
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: 5, padding: '10px 14px',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                  }}
                >
                  {active && (
                    <motion.div
                      layoutId="activeTabDesktop"
                      transition={{ type: 'spring', stiffness: 500, damping: 32 }}
                      style={{
                        position: 'absolute', inset: 0, borderRadius: 18,
                        background: 'linear-gradient(135deg, var(--sl-accent) 0%, var(--sl-accent-bright) 100%)',
                        boxShadow: '0 4px 14px var(--sl-accent-muted)',
                      }}
                    />
                  )}
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <Icon
                      style={{
                        width: 20, height: 20,
                        color: active ? '#fff' : 'var(--sl-nav-icon)',
                        transition: 'color 0.2s ease',
                      }}
                      strokeWidth={active ? 2.5 : 2}
                    />
                  </div>
                  <span
                    style={{
                      position: 'relative', zIndex: 1, fontSize: 11, fontWeight: 600,
                      color: active ? '#fff' : 'var(--sl-nav-icon)',
                      transition: 'color 0.2s ease',
                      fontFamily: "'Jost', sans-serif",
                    }}
                  >
                    {label}
                  </span>
                </motion.button>
              )
            })}

            {/* Separator + theme toggle */}
            <div style={{ width: 1, height: 40, background: 'var(--sl-surface-border)', marginLeft: 4 }} />
            <motion.button
              whileTap={{ scale: 0.85, rotate: isDark ? 30 : -30 }}
              onClick={toggleTheme}
              aria-label={isDark ? t(lang, 'nav_light') : t(lang, 'nav_dark')}
              style={{
                width: 48, height: 48, borderRadius: 16, flexShrink: 0,
                background: 'var(--sl-accent-muted)',
                border: '1px solid var(--sl-accent-border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', marginLeft: 4,
              }}
            >
              {isDark
                ? <Sun style={{ width: 18, height: 18, color: 'var(--sl-accent)' }} />
                : <Moon style={{ width: 18, height: 18, color: 'var(--sl-accent)' }} />
              }
            </motion.button>
          </div>
        </div>
      </motion.div>
    </>
  )
}

export default UltraResponsiveNav
